import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import moment from "moment";

const UBODialog = ({ open, onClose, ubo, onSave, customerId }) => {
  const [formData, setFormData] = useState({
    name: "",
    idDocument: null,
    pepDeclaration: null,
    badPressCheck: {
      noFinancialCrimes: false,
      noFinancialMisconduct: false,
      noNegativePublications: false,
      notOnSanctionList: false,
      noEnvironmentalViolations: false,
      noReputationDamage: false,
    },
  });

  const [idDocumentFile, setIdDocumentFile] = useState(null);
  const [pepDeclarationFile, setPepDeclarationFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (ubo) {
      setFormData({
        name: ubo.name || "",
        idDocument: ubo.idDocument,
        pepDeclaration: ubo.pepDeclaration,
        badPressCheck: ubo.badPressCheck || {
          noFinancialCrimes: false,
          noFinancialMisconduct: false,
          noNegativePublications: false,
          notOnSanctionList: false,
          noEnvironmentalViolations: false,
          noReputationDamage: false,
        },
      });
    }
  }, [ubo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBadPressCheckChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      badPressCheck: {
        ...prev.badPressCheck,
        [field]: e.target.checked,
      },
    }));
  };

  const handleIdDocumentDrop = async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setIdDocumentFile(file);
      try {
        const fileInfo = {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        };
        const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
        const fileId = fileInfoRes.data.fileId;

        const formData = new FormData();
        formData.append("file", file, fileId);
        await axios.post("/api/v1/azureblobstorage", formData);

        setFormData((prev) => ({
          ...prev,
          idDocument: fileId,
        }));
      } catch (error) {
        console.error("Error uploading ID document:", error);
      }
    }
  };

  const handlePepDeclarationDrop = async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setPepDeclarationFile(file);
      try {
        const fileInfo = {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          creationDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
        };
        const fileInfoRes = await axios.post("/api/v1/file", fileInfo);
        const fileId = fileInfoRes.data.fileId;

        const formData = new FormData();
        formData.append("file", file, fileId);
        await axios.post("/api/v1/azureblobstorage", formData);

        setFormData((prev) => ({
          ...prev,
          pepDeclaration: fileId,
        }));
      } catch (error) {
        console.error("Error uploading PEP declaration:", error);
      }
    }
  };

  const { getRootProps: getIdDocumentRootProps, getInputProps: getIdDocumentInputProps } = useDropzone({
    onDrop: handleIdDocumentDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
  });

  const { getRootProps: getPepDeclarationRootProps, getInputProps: getPepDeclarationInputProps } = useDropzone({
    onDrop: handlePepDeclarationDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxFiles: 1,
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const uboData = {
        ...formData,
        id: ubo?.id,
        entityId: customerId,
        isComplete:
          formData.name && formData.idDocument && formData.pepDeclaration && Object.values(formData.badPressCheck).every(Boolean),
      };
      await onSave(uboData);
    } catch (error) {
      console.error("Error saving UBO:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{ubo?.id ? "Edit UBO" : "Add UBO"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField name="name" label="Full Name" value={formData.name} onChange={handleChange} fullWidth required />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                ID/Passport Document
              </Typography>
              <Box
                {...getIdDocumentRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 1,
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: formData.idDocument ? "success.light" : "background.paper",
                }}
              >
                <input {...getIdDocumentInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
                <Typography>
                  {formData.idDocument ? "ID Document uploaded" : "Drop ID/Passport document here or click to select"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PDF only
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                PEP Declaration
              </Typography>
              <Box
                {...getPepDeclarationRootProps()}
                sx={{
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 1,
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: formData.pepDeclaration ? "success.light" : "background.paper",
                }}
              >
                <input {...getPepDeclarationInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
                <Typography>
                  {formData.pepDeclaration ? "PEP Declaration uploaded" : "Drop PEP declaration here or click to select"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PDF only
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Bad Press Check
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.noFinancialCrimes}
                      onChange={handleBadPressCheckChange("noFinancialCrimes")}
                    />
                  }
                  label="Not involved in financial crimes"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.noFinancialMisconduct}
                      onChange={handleBadPressCheckChange("noFinancialMisconduct")}
                    />
                  }
                  label="No fraud, corruption, tax evasion, money laundering or terrorism"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.noNegativePublications}
                      onChange={handleBadPressCheckChange("noNegativePublications")}
                    />
                  }
                  label="No negative publications"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.notOnSanctionList}
                      onChange={handleBadPressCheckChange("notOnSanctionList")}
                    />
                  }
                  label="Not on any sanction list"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.noEnvironmentalViolations}
                      onChange={handleBadPressCheckChange("noEnvironmentalViolations")}
                    />
                  }
                  label="No environmental violations"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.badPressCheck.noReputationDamage}
                      onChange={handleBadPressCheckChange("noReputationDamage")}
                    />
                  }
                  label="No serious reputation damage"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !formData.name}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UBODialog;
